import React, { useCallback } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { Dialog, FilterList } from '@modernloop/shared/components';
import { Box, ListItem, ListItemButton } from '@mui/material';

import { AtsInterviewDefinitionFragment } from 'src/generated/mloop-graphql';

import ScorecardIllustration from 'src/assets/images/illustrations/scorecard_64@2x.png';

import {
  useGetChildSize,
  useGroupBy,
  useNoScorecardLabel,
  useRenderGroup,
  useRenderOption,
  useScorecardSearchPlaceholder,
  useScorecardSelectTitle,
} from 'src/entities/Ats/AtsScorecard';
import useAtsScorecardOptions from 'src/entities/Ats/useAtsScorecardOptions';

type Props = {
  atsJobId: string;
  onClose: () => void;
  onSelectScorecard: (scorecard: AtsInterviewDefinitionFragment) => void;
};

const AtsScorecardModal = ({ atsJobId, onClose, onSelectScorecard }: Props): JSX.Element => {
  const { t } = useTranslation();
  const { loading, options } = useAtsScorecardOptions(atsJobId);

  const handleScorecardSelectClose = (scorecard: AtsInterviewDefinitionFragment) => {
    onSelectScorecard(scorecard);
    onClose();
  };

  const renderOptionImageBlock = useRenderOption();

  const renderOption = useCallback(
    (props: React.HTMLAttributes<HTMLElement>, option: AtsInterviewDefinitionFragment) => {
      return (
        <ListItem
          {...props}
          data-testid="ats-scorecard-option"
          data-scorecard-name={option.name ?? 'none'}
          data-scorecard-id={option.atsId}
          key={option.atsId}
        >
          <ListItemButton>{renderOptionImageBlock(option)}</ListItemButton>
        </ListItem>
      );
    },
    [renderOptionImageBlock]
  );

  const renderGroup = useRenderGroup();
  const getChildSize = useGetChildSize();
  const groupBy = useGroupBy();
  const noScorecardLabel = useNoScorecardLabel();
  const scorecardSelectTitle = useScorecardSelectTitle();
  const scorecardSearchPlaceholder = useScorecardSearchPlaceholder();

  return (
    <Dialog
      open
      maxWidth="xs"
      decoration={
        <img
          style={{ width: '64px', height: '64px' }}
          src={ScorecardIllustration}
          alt={t('hiring-dashboard-nav:nav.scorecards.scorecardAlt')}
        />
      }
      title={scorecardSelectTitle}
      cancelOptions={{
        label: t('hiring-dashboard-common:common.cancel'),
        onClick: onClose,
      }}
      onClose={onClose}
    >
      <Box height={460}>
        <FilterList
          placeholderText={scorecardSearchPlaceholder}
          loading={loading}
          options={options}
          getOptionLabel={(option) => option.name ?? noScorecardLabel}
          groupBy={groupBy}
          renderOption={renderOption}
          renderGroup={renderGroup}
          onChange={(event: React.ChangeEvent, values: AtsInterviewDefinitionFragment[]) => {
            if (values && values.length > 0) {
              handleScorecardSelectClose(values[0]);
            }
          }}
          getChildSize={getChildSize}
          autoFocus
        />
      </Box>
    </Dialog>
  );
};

export default AtsScorecardModal;
